import { useMutation, useSuspenseQuery } from '@apollo/client';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { FC, Suspense, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ActionButtons from 'src/components/ActionButtons';
import Loading from 'src/components/Loading';
import { CheckoutData, FormInputDate, FormInputText } from 'src/components/form/';
import { FormCheckbox } from 'src/components/form/FormCheckbox';
import { Access, AuthorizationWrapper } from 'src/features/authorization';
import { HeaderDeviceConfiguration } from 'src/features/createDeviceConfiguration/components';
import {
  defaultFormValues,
  initialStateOptions,
  ENABLED_SENSOR_TYPES,
} from 'src/features/createDeviceConfiguration/data/initialData';
import CREATE_DEVICE_PROFILE from 'src/features/createDeviceConfiguration/graphql/mutations/createProfile';
import CONFIRM_DEVICE_DOCUMENT_UPLOAD from 'src/features/createDeviceConfiguration/graphql/mutations/confirmDeviceDocumentUpload';
import useHardwareOptions from 'src/features/createDeviceConfiguration/hooks/useHardwareOptions';
import { tabStyle } from 'src/features/createDeviceConfiguration/styles/styles';
import { FormValues } from 'src/features/createDeviceConfiguration/type/FormValues';
import DEVICE_PROFILE from 'src/graphql/querys/deviceProfile';
import FETCH_DEVICE_PROFILE from 'src/graphql/querys/fetchDeviceProfile';
import { useModalStatusMessage, useUploadS3Images } from 'src/hooks';
import { setSaveHardwareConfigurationModal } from 'src/slices/modals';
import { useDispatch, useSelector } from 'src/store';
import GET_UPDATE_HARDWARE_CONFIGURATION_DATA, {
  Device,
} from '../graphql/querys/getUpdateHardwareConfigurationData';
import OptionSelector from './OptionSelector';
import SensorizationTabContent from './tabs/sensorization/SensorizationTabContent';
import GeneralTabContent from './tabs/general/GeneralTabContent';

interface Props {
  deviceId: string;
  onClose: () => void;
}

const isIntegration = (device: Device) => {
  return device?.profile?.source === 'integration';
};

const Component: FC<Props> = ({ deviceId, onClose: handleClose }) => {
  const {
    data: { device },
  } = useSuspenseQuery(GET_UPDATE_HARDWARE_CONFIGURATION_DATA, {
    variables: {
      deviceId,
    },
  });

  const [check, setCheck] = useState(initialStateOptions);
  const [tabValue, setTabValue] = useState(0);
  const { uploadS3Images } = useUploadS3Images('amaru-news');

  const { options: energizationOptions } = useHardwareOptions('energization');
  const { options: powerSupplyVoltageOptions } = useHardwareOptions('power_supply');
  const { options: batteryCapacityOptions } = useHardwareOptions('battery_capacity');
  const { options: batteryVoltageOptions } = useHardwareOptions('battery_voltage');
  const { options: technologyOptions } = useHardwareOptions('battery_tech');
  const { options: cpuOptions } = useHardwareOptions('cpu');
  const { options: auxiliaryOptions } = useHardwareOptions('auxiliary_plate');
  const { options: internetConnectionOptions } = useHardwareOptions('internet_connection');
  const { options: simModelOptions } = useHardwareOptions('sim_model');
  const { options: mobileOperatorOptions } = useHardwareOptions('mobile_operator');
  const { options: dataPlanMobileOptions } = useHardwareOptions('data_plan_type');
  const { options: antennaOptions } = useHardwareOptions('antenna_model');
  const { options: satellitalTypeOptions } = useHardwareOptions('satellital_type');
  const { options: dataConnectionOptions } = useHardwareOptions('data_connection_type');
  const { options: signalModuleOptions } = useHardwareOptions('signal_module');
  const { options: tdiModelOptions } = useHardwareOptions('tdi_model');

  const classes = tabStyle;
  const [createProfile, { loading, error }] = useMutation(CREATE_DEVICE_PROFILE, {
    refetchQueries: [FETCH_DEVICE_PROFILE, DEVICE_PROFILE, GET_UPDATE_HARDWARE_CONFIGURATION_DATA],
  });
  const [confirmDeviceDocumentUpload] = useMutation(CONFIRM_DEVICE_DOCUMENT_UPLOAD);

  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const {
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<FormValues>();
  const [
    energization,
    internetConnection,
    mobileOperator,
    satellitalType,
  ] = watch([
    'energizationOption',
    'internetConnectionOption',
    'mobileOperatorOption',
    'satellitalTypeOption',
  ]);

  const watchedSensors = watch('sensors');
  const hasAny4_20 = watchedSensors?.some(s => s?.attributes?.protocol === '4-20mA') ?? false;
  const hasFlujSensor = (device?.sensors ?? []).some(s => s.type.label === 'Flujo');

  const parentDevice = device?.relatedDevices?.find(rel => rel.relation === 'PARENT')?.device;
  const isChildDevice = !!parentDevice;

  useEffect(() => {
    if (device) {
      // TODO: SOLVE TYPE ERROR
      reset(
        device?.hardwareConfiguration !== null
          ? {
            ...device.hardwareConfiguration,
            images: device?.hardwareConfiguration?.images
              ? { url: device?.hardwareConfiguration?.images ?? '' }
              : null,
            nameProject: device?.hardwareConfiguration?.nameProject ?? '',
            dateAction: device?.hardwareConfiguration?.dateAction
              ? new Date(device?.hardwareConfiguration?.dateAction)
              : new Date(),
            sensors:
              device?.sensors.filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? "")).map((sensor) => ({
                sensorModelOption: sensor?.configuration?.sensorModelOption ?? null,
                serialNumber: sensor?.configuration?.serialNumber ?? '',
                modbusId: sensor?.configuration?.modbusId ?? '',
                batchNumber: sensor?.configuration?.batchNumber ?? '',
                integrationDetail: sensor?.configuration?.integrationDetail ?? '',
                factorM: sensor?.configuration?.factorM ?? null,
                offsetN: sensor?.configuration?.offsetN ?? null,
                attributes: sensor?.configuration?.attributes ? JSON.parse(sensor.configuration.attributes) : null,
              })) ?? [],
          }
          : { ...defaultFormValues },
      );
      const optionsCheck = {
        powerSupply: device?.hardwareConfiguration?.powerSupplyVoltageOption ? true : false,
      };
      setCheck(optionsCheck);
    }
  }, [device, reset]);

  const watchFlowPulse = watch('flowPulse');
  useEffect(() => {
    if (!watchFlowPulse) {
      setValue('flowPulseTdiOption', null);
      setValue('flowPulseVoltage', null);
      setValue('flowPulseTdiChannel', null);
    }
  }, [watchFlowPulse, setValue]);

  const handleCloseDialog = () => {
    setCheck(initialStateOptions);
    setTabValue(0);
    handleClose();
    reset();
  };


  const handleClickCheck = (checkData: { powerSupply: boolean }) => {
    if (!checkData.powerSupply) {
      setValue('powerSupplyVoltageOption', null);
      setValue('powerSupplyModel', null);
      setValue('powerSupplySerialNumber', null);
      setValue('powerSupplyBrand', null, { shouldDirty: true });
    }
    setCheck(checkData);
  };

  const onSubmit = async (vars: FormValues) => {
    let photoUrl = vars.images?.url ?? '';
    const img = vars?.images?.file ?? null;
    if (img) {
      photoUrl = await uploadS3Images(img);
    }
    try {
      const input = {
        // General
        images: photoUrl,
        googleDriveUrl: vars.googleDriveUrl,
        deviceId: device?.id,
        nameProject: vars.nameProject,
        lockNumber: vars.lockNumber,
        dateAction: vars.dateAction,
        observation: vars.observation,
        electricalPlanUrl: vars.electricalPlanUrl ?? null,
        diagramUrl: vars.diagramUrl ?? null,
        tcpExtraction: vars.tcpExtraction ?? null,
        tcpExtractionIp: vars.tcpExtractionIp ?? null,
        batchNumberSolarController: vars.batchNumberSolarController ?? null,
        cableNetworkType: vars.cableNetworkType ?? null,
        ethernetMaintenance: vars.ethernetMaintenance ?? null,

        // Sensorization
        sensors: (() => {
            const enabledSensors = device?.sensors.filter(
              s => ENABLED_SENSOR_TYPES.includes(s.type.label ?? '')
            ) ?? [];
            return (vars.sensors ?? []).map((sensor, index) => ({
              sensorId: enabledSensors[index]?.id,
              sensorModel: sensor.sensorModelOption?.id ?? null,
              serialNumber: sensor.serialNumber,
              modbusId: sensor.modbusId,
              batchNumber: sensor.batchNumber,
              integrationDetail: sensor.integrationDetail,
              factorM: sensor.factorM,
              offsetN: sensor.offsetN,
              attributes: sensor.attributes,
            }));
          })(),

        // Energization
        energization: vars.energizationOption?.id ?? null,
        powerSupplyVoltage: vars.powerSupplyVoltageOption?.id ?? null,
        powerSupplyModel: vars.powerSupplyModel,
        powerSupplySerialNumber: vars.powerSupplySerialNumber,
        powerSupplyBrand: vars.powerSupplyBrand,
        solarPanelsControllers: vars.solarPanelsControllers,
        numberSolarPanelsControllers: vars.numberSolarPanelsControllers,
        quantityBattery: vars.quantityBattery,
        modelBattery: vars.modelBattery,
        technology: vars.technologyOption?.id ?? null,
        battery: vars.batteryCapacityOption?.id ?? null,
        voltage: vars.batteryVoltageOption?.id ?? null,
        quantitySun: vars.quantitySun,
        modelsSun: vars.modelsSun,
        watts: vars.watts,
        numberSun: vars.numberSun,

        // Data logger
        cpu: vars.cpuOption?.id ?? null,
        batchNumberCpu: vars.batchNumberCpu,
        auxiliary: vars.auxiliaryOption?.id ?? null,
        numberAuxiliary: vars.numberAuxiliary,
        batchNumberAuxiliary: vars.batchNumberAuxiliary,
        codeNumberWidgetlord: vars.codeNumberWidgetlord,
        batchNumberWidgetlord: vars.batchNumberWidgetlord,
        serialNumberWidgetlord: vars.serialNumberWidgetlord,
        imageVersion: vars.imageVersion,
        usbExtraction: vars.usbExtraction,
        signalReader: vars.signalReaderOption?.id ?? null,
        signalReaderChannel: vars.signalReaderChannel ?? null,
        flowPulse: vars.flowPulse ?? null,
        flowPulseTdiModel: vars.flowPulseTdiOption?.id ?? null,
        flowPulseVoltage: vars.flowPulseVoltage ?? null,
        flowPulseTdiChannel: vars.flowPulseTdiChannel ?? null,


        // Connectivity
        dataConnectionType: vars.dataConnectionOption?.id ?? null,
        internetConnection: vars.internetConnectionOption?.id ?? null,
        modelsConnection: vars.modelsConnectionOption?.id ?? null,
        batchNumberSimCard: vars.batchNumberSimCard,
        approved: vars.approved,
        mobileOperator: vars.mobileOperatorOption?.id ?? null,
        idEmnify: vars.idEmnify,
        dataPlanType: vars.dataPlanMobileOption?.id ?? null,
        antenna: vars.antennaOption?.id ?? null,
        ip: vars.ip,
        mac: vars.mac,
        configurationIp: vars.configurationIp,
        administrator: vars.administrator,
        networkName: vars.networkName,
        iccid: vars.iccid,
        imei: vars.imei,
        satellitalType: vars.satellitalTypeOption?.id ?? null,
        phoneNumber: vars.phoneNumber,
        satellitalSv: vars.satellitalSv,
        satellitalHv: vars.satellitalHv,
        satellitalHardwareName: vars.satellitalHardwareName,
        satellitalSerialName: vars.satellitalSerialName,
      };

      const result = await createProfile({
        variables: {
          input: input,
        },
      });
      
      const profileId = result?.data?.createDeviceProfile?.deviceProfileId;
      if (result.data.createDeviceProfile.success && profileId) {
        // Confirmar uploads de documentos
        const confirmPromises = [];
        if (vars.electricalPlanFileKey) {
          confirmPromises.push(
            confirmDeviceDocumentUpload({
              variables: {
                deviceProfileId: profileId,
                documentType: 'ELECTRICAL_PLAN',
                fileKey: vars.electricalPlanFileKey,
              },
            })
          );
        }
        if (vars.diagramFileKey) {
          confirmPromises.push(
            confirmDeviceDocumentUpload({
              variables: {
                deviceProfileId: profileId,
                documentType: 'DIAGRAM',
                fileKey: vars.diagramFileKey,
              },
            })
          );
        }
        await Promise.all(confirmPromises);
        handleCloseDialog();
        const successMsg =
          device?.hardwareConfiguration !== null
            ? 'Configuración actualizada correctamente.'
            : 'Configuración creada correctamente.';
        openModalSuccessMessage(successMsg);
      } else {
        openModalErrorMessage('Error, vuelve a intentarlo.');
      }
    } catch (e) {
      console.log({e})
      openModalErrorMessage('Error, vuelve a intentarlo.');
    }
  };

  const handleTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <>
        <HeaderDeviceConfiguration device={device} />
        <Divider />
        <DialogContent>
          <Loading />
        </DialogContent>
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderDeviceConfiguration device={device} />
        <Divider />
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="error">
                {error.message}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
      </>
    );
  }

  return (
    <>
      <HeaderDeviceConfiguration device={device} />
      <Divider />
      <Grid
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabValue}
          variant="fullWidth"
          centered
          sx={{ width: '100%' }}
          TabIndicatorProps={{ sx: { display: 'none' } }}
        >
          <Tab
            label="General"
            sx={{
              ...(isIntegration(device)
                ? classes.singleTab
                : tabValue === 0
                  ? classes.selectedLeftTab
                  : classes.leftTab),
            }}
          />
          {!isIntegration(device) && (
            <Tab
              label="Sensorización"
              sx={{ ...(tabValue === 1 ? classes.selectedTab : classes.tab) }}
            />
          )}
          {!isIntegration(device) && (
            <Tab
              label="Energización"
              sx={{ ...(tabValue === 2 ? classes.selectedTab : classes.tab) }}
            />
          )}
          {!isIntegration(device) && (
            <Tab
              label="Data logger"
              sx={{ ...(tabValue === 3 ? classes.selectedTab : classes.tab) }}
            />
          )}
          {!isIntegration(device) && (
            <Tab
              label="Conectividad"
              sx={{ ...(tabValue === 4 ? classes.selectedRightTab : classes.rightTab) }}
            />
          )}
        </Tabs>
      </Grid>

      <DialogContent
        sx={{
          height: '60vh',
          justifyContent: 'center',
        }}
      >
        <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
          {/* GENERAL */}

          {tabValue === 0 && (
            <GeneralTabContent
              device={device}
              control={control}
              isChildDevice={isChildDevice}
              setValue={setValue}
            />
          )}

          {/* SENSORIZACION */}
          {tabValue === 1 && (
            <SensorizationTabContent
              device={device}
              control={control}
              setValue={setValue}
            />
          )}

          {/* ENERGIZACION */}
          {tabValue === 2 && (
            <Grid item xs={12} sm={6} p={2} mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <OptionSelector
                    isDisabled={isChildDevice}
                    options={energizationOptions}
                    label="Tipo de Energía *"
                    name="energizationOption"
                    control={control}
                  />
                </Grid>
                {(energization?.name === 'Solar' ||
                  energization?.name === 'Red 220VAC y Solar') && (
                    <>
                      <Typography variant="h6" color="textSecondary" mt={2} ml={2}>
                        Controlador Solar
                      </Typography>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="solarPanelsControllers"
                          control={control}
                          label="Modelo controlador solar *"
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="numberSolarPanelsControllers"
                          control={control}
                          label="Número de serie del controlador solar *"
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="batchNumberSolarController"
                          control={control}
                          label="Número de lote del controlador solar"
                        />
                      </Grid>
                      <Typography variant="h6" color="textSecondary" mt={2} ml={2}>
                        Panel Solar
                      </Typography>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="quantitySun"
                          control={control}
                          label="Cantidad de paneles solares *"
                          type="number"
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="modelsSun"
                          control={control}
                          label="Modelo del panel solar *"
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice} name="watts" control={control} label="Potencia[W]" />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="numberSun"
                          control={control}
                          label="Número de serie del panel solar *"
                        />
                      </Grid>
                    </>
                  )}

                {energization && energization?.name !== 'Red 220VAC' && (
                  <>
                    <Typography variant="h6" color="textSecondary" mt={2} ml={2}>
                      Batería
                    </Typography>
                    <Grid item xs={12} mt={2}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="quantityBattery"
                        control={control}
                        label="Cantidad de baterías *"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} mt={2}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="modelBattery"
                        control={control}
                        label="Marca o modelo de batería"
                      />
                    </Grid>
                    <Grid item xs={12} mt={2}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={technologyOptions}
                        label="Tecnología *"
                        name="technologyOption"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12} mt={2}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={batteryCapacityOptions}
                        label="Capacidad de la batería *"
                        name="batteryCapacityOption"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12} mt={2}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={batteryVoltageOptions}
                        label="Voltage del sistema *"
                        name="batteryVoltageOption"
                        control={control}
                      />
                    </Grid>
                  </>
                )}
                {energization?.name === 'Solar' && (
                  <Grid item xs={12} mt={2}>
                    <CheckoutData
                      check={check}
                      setCheck={handleClickCheck}
                      title="¿Dispositivo tiene fuente AC/DC?"
                      name="powerSupply"
                    />
                  </Grid>
                )}
                {(check?.powerSupply === true ||
                  (energization && energization?.name !== 'Solar')) && (
                    <>
                      <Typography variant="h6" color="textSecondary" mt={2} ml={2}>
                        Fuente {energization?.name !== 'Solar' ? 'DC/DC' : 'AC/DC'}
                      </Typography>

                      <Grid item xs={12} mt={2}>
                        <OptionSelector
                          isDisabled={isChildDevice}
                          options={powerSupplyVoltageOptions}
                          label="Voltage *"
                          name="powerSupplyVoltageOption"
                          control={control}
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice} name="powerSupplyModel" control={control} label="Modelo" />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="powerSupplySerialNumber"
                          control={control}
                          label="Número de serie *"
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <FormInputText
                          disabled={isChildDevice} name="powerSupplyBrand" control={control} label="Marca" />
                      </Grid>
                    </>
                  )}
              </Grid>
            </Grid>
          )}

          {/* DATA LOGGER */}
          {tabValue === 3 && (
            <Grid item xs={12} sm={6} p={2} mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <OptionSelector
                    isDisabled={isChildDevice}
                    options={cpuOptions}
                    label="CPU central *"
                    name="cpuOption"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="batchNumberCpu"
                    control={control}
                    label="Número de lote CPU *"
                  />
                </Grid>
                <Grid item xs={12}>
                  <OptionSelector
                    isDisabled={isChildDevice}
                    options={auxiliaryOptions}
                    label="Placa auxiliar *"
                    name="auxiliaryOption"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="numberAuxiliary"
                    control={control}
                    label="Número de serie de la placa auxiliar *"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="batchNumberAuxiliary"
                    control={control}
                    label="Número de lote placa auxiliar *"
                  />
                </Grid>
                {hasAny4_20 && (
                  <>
                    <Grid item xs={12}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={signalModuleOptions}
                        label="Módulo de señal"
                        name="signalReaderOption"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="signalReaderChannel"
                        control={control}
                        label="Channel"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="batchNumberWidgetlord"
                    control={control}
                    label="Número de lote widgetlord"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="codeNumberWidgetlord"
                    control={control}
                    label="Código widgetlord"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="imageVersion"
                    control={control}
                    label="Versión de la imagen *"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormCheckbox
                    disabled={isChildDevice}
                    name="usbExtraction"
                    control={control}
                    label="Extracción USB"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormCheckbox
                    disabled={isChildDevice}
                    name="tcpExtraction"
                    control={control}
                    label="Extracción TCP"
                  />
                </Grid>
                {watch('tcpExtraction') && (
                  <Grid item xs={12}>
                    <FormInputText
                      disabled={isChildDevice}
                      name="tcpExtractionIp"
                      control={control}
                      label="IP fija TCP"
                    />
                  </Grid>
                )}
                {hasFlujSensor && (
                  <>
                    <Grid item xs={12}>
                      <FormCheckbox
                        disabled={isChildDevice}
                        name="flowPulse"
                        control={control}
                        label="Con Pulso"
                      />
                    </Grid>
                    {watch('flowPulse') && (
                      <>
                        <Grid item xs={12}>
                          <OptionSelector
                            isDisabled={isChildDevice}
                            options={tdiModelOptions}
                            label="Modelo TDI"
                            name="flowPulseTdiOption"
                            control={control}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            name="flowPulseVoltage"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                label="Voltaje TDI"
                                disabled={isChildDevice}
                                value={field.value ?? ''}
                              >
                                <MenuItem value="12">12V</MenuItem>
                                <MenuItem value="24">24V</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            disabled={isChildDevice}
                            name="flowPulseTdiChannel"
                            control={control}
                            label="Channel"
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          )}

          {/* CONECTIVIDAD */}
          {tabValue === 4 && (
            <Grid item xs={12} sm={6} p={2} mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <OptionSelector
                    isDisabled={isChildDevice}
                    options={dataConnectionOptions}
                    label="Método de conexión de datos  *"
                    name="dataConnectionOption"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <OptionSelector
                    isDisabled={isChildDevice}
                    options={internetConnectionOptions}
                    label="Método de conexión a internet  *"
                    name="internetConnectionOption"
                    control={control}
                  />
                </Grid>
                {internetConnection?.name === 'Redes Móviles' && (
                  <>
                    <Grid item xs={12}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={simModelOptions}
                        label="Modelo *"
                        name="modelsConnectionOption"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="imei"
                        control={control}
                        label="IMEI *"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="batchNumberSimCard"
                        control={control}
                        label="Número de lote módulo 4G*"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormCheckbox
                        disabled={isChildDevice}
                        name="approved"
                        control={control}
                        label="Homologado?"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={mobileOperatorOptions}
                        label="Operador móvil *"
                        name="mobileOperatorOption"
                        control={control}
                      />
                    </Grid>
                    {mobileOperator?.name === 'Emnify' && (
                      <Grid item xs={12}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="idEmnify"
                          control={control}
                          label="ID Emnify *"
                        />
                      </Grid>
                    )}
                    {(mobileOperator?.name === 'Emnify' || mobileOperator?.name === 'Mint') && (
                      <>
                        <Grid item xs={12}>
                          <FormInputText
                            disabled={isChildDevice}
                            name="phoneNumber"
                            control={control}
                            label="Número de Teléfono"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <OptionSelector
                            isDisabled={isChildDevice}
                            options={dataPlanMobileOptions}
                            label="Plan de datos"
                            name="dataPlanMobileOption"
                            control={control}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="iccid"
                        control={control}
                        label="ICCID *"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={antennaOptions}
                        label="Modelo antena *"
                        name="antennaOption"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="ip"
                        control={control}
                        label="Dirección IP"
                      />
                    </Grid>
                  </>
                )}
                {(internetConnection?.name === 'Red Cableada' ||
                  internetConnection?.name === 'Red Inalámbrica') && (
                    <>
                      {internetConnection?.name === 'Red Cableada' && (
                        <Grid item xs={12}>
                          <Controller
                            name="cableNetworkType"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                label="Tipo de IP"
                                disabled={isChildDevice}
                                value={field.value ?? ''}
                              >
                                <MenuItem value="fija">IP Fija</MenuItem>
                                <MenuItem value="dhcp">DHCP</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="mac"
                          control={control}
                          label="MAC *"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="configurationIp"
                          control={control}
                          label="Configuración IP *"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          disabled={isChildDevice}
                          name="administrator"
                          control={control}
                          label="Administrador *"
                        />
                      </Grid>
                    </>
                  )}
                {internetConnection?.name === 'Red Inalámbrica' && (
                  <>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="networkName"
                        control={control}
                        label="Nombre Red *"
                      />
                    </Grid>
                  </>
                )}
                {internetConnection?.name === 'Internet Satelital' && (
                  <>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="iccid"
                        control={control}
                        label="ICCID *"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="imei"
                        control={control}
                        label="IMEI *"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <OptionSelector
                        isDisabled={isChildDevice}
                        options={satellitalTypeOptions}
                        label="Tipo *"
                        name="satellitalTypeOption"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="phoneNumber"
                        control={control}
                        label="Número de Teléfono"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        disabled={isChildDevice}
                        name="satellitalHardwareName"
                        control={control}
                        label="Nombre del hardware"
                      />
                    </Grid>
                    {satellitalType?.name === 'Rock Remote' && (
                      <>
                        <Grid item xs={12}>
                          <FormInputText
                            disabled={isChildDevice}
                            name="satellitalSerialName"
                            control={control}
                            label="Serial de hardware"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            disabled={isChildDevice}
                            name="satellitalSv"
                            control={control}
                            label="Versión de software"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            disabled={isChildDevice}
                            name="satellitalHv"
                            control={control}
                            label="Versión de hardware"
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <FormInputText
                    disabled={isChildDevice}
                    name="ethernetMaintenance"
                    control={control}
                    label="Ethernet de mantenimiento"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          padding: '20px 24px',
        }}
      >
        <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <Grid item xs={12} sm={8} p={2}>
            <ActionButtons
              closeFunction={handleCloseDialog}
              actionFunction={handleSubmit(onSubmit)}
              actionButtonText={device?.hardwareConfiguration !== null ? 'Actualizar' : 'Crear'}
              actionButtonColor="success"
              disabledAction={!isDirty}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

const Fallback: FC = () => null;

const SaveHardwareConfigurationModal: FC = () => {
  const dispatch = useDispatch();
  const { saveHardwareConfigurationModal: modal } = useSelector((state) => state.modals_store);

  const onClose = () => {
    dispatch(
      setSaveHardwareConfigurationModal({
        open: false,
      }),
    );
  };

  return (
    <Dialog open={modal.open} maxWidth="xl" fullWidth>
      <Suspense fallback={<Fallback />}>
        <AuthorizationWrapper onClose={onClose} access={Access.SaveHardwareConfigurationModal}>
          {modal.open && <Component onClose={onClose} deviceId={modal.deviceId} />}
        </AuthorizationWrapper>
      </Suspense>
    </Dialog>
  );
};

export default SaveHardwareConfigurationModal;
